import { useState } from "react";
import axios from "axios";
import { KeyRound, Inbox, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Q_LABELS = {
  career_change: "Career change",
  relationships: "Relationships",
  health_body: "Health & body",
  purpose_identity: "Purpose & identity",
  money_security: "Money & security",
  other: "Other / mix",
  no_clarity: "Don't know where to start",
  fear_of_failure: "Scared of failing",
  no_time: "No time or energy",
  no_support: "No support",
  disbelief: "Doesn't believe it's possible",
  just_started: "Just started",
  few_months: "A few months",
  over_a_year: "Over a year",
  years: "Years",
};

const Chip = ({ active, onClick, label, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors duration-150 ${
      active
        ? "bg-[#B25E3B] text-[#FAF7F2] border-[#B25E3B]"
        : "bg-white text-[#5A4D46] border-[#E9DFD6] hover:border-[#B25E3B]"
    }`}
  >
    {label}
  </button>
);

export const Admin = () => {
  const [key, setKey] = useState(sessionStorage.getItem("admin_key") || "");
  const [submissions, setSubmissions] = useState(null);
  const [error, setError] = useState("");
  const [filterQ1, setFilterQ1] = useState(null);
  const [filterQ4, setFilterQ4] = useState(null);

  const load = async (e) => {
    e?.preventDefault();
    setError("");
    try {
      const res = await axios.get(`${API}/submissions`, {
        headers: { "X-Admin-Key": key },
      });
      sessionStorage.setItem("admin_key", key);
      setSubmissions(res.data);
    } catch {
      setError("Wrong key — please check and try again.");
      setSubmissions(null);
    }
  };

  const DURATION_WEIGHT = { years: 3, over_a_year: 2, few_months: 1, just_started: 0 };
  const score = (s) => s.q4 * 10 + (DURATION_WEIGHT[s.q3] || 0);

  const Q1_KEYS = ["career_change", "relationships", "health_body", "purpose_identity", "money_security", "other"];

  const exportCSV = () => {
    const header = ["timestamp", "name", "email", "stuck_on", "stopping_them", "duration", "readiness", "result_shown"];
    const rows = filtered.map((s) =>
      [s.timestamp, s.name || "", s.email || "", s.q1, s.q2, s.q3, s.q4, s.result_id]
        .map((v) => `"${String(v).replaceAll('"', '""')}"`)
        .join(",")
    );
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `gray-roots-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const toggleBooked = async (s) => {
    try {
      await axios.post(
        `${API}/submissions/${s.id}/booked`,
        { booked: !s.booked },
        { headers: { "X-Admin-Key": key } }
      );
      setSubmissions(
        submissions.map((x) => (x.id === s.id ? { ...x, booked: !s.booked } : x))
      );
    } catch {
      setError("Couldn't update that one — please try again.");
    }
  };

  if (!submissions) {
    return (
      <div data-testid="admin-login" className="w-full max-w-sm mx-auto pt-16 px-4">
        <div className="rounded-3xl bg-white border border-[#E9DFD6] p-8">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FBEBE4] mb-4">
            <KeyRound className="w-5 h-5 text-[#B25E3B]" />
          </span>
          <h1 className="font-serif-display text-2xl text-[#2C221E] mb-2">Advisor view</h1>
          <p className="text-sm text-[#85746C] mb-6">
            Enter your admin key to see quiz submissions.
          </p>
          <form onSubmit={load} className="flex flex-col gap-3">
            <input
              data-testid="admin-key-input"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Admin key"
              className="w-full rounded-xl border border-[#E9DFD6] bg-[#FCF9F5] px-4 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-[#B25E3B]"
            />
            <button
              data-testid="admin-login-btn"
              type="submit"
              className="w-full rounded-full bg-[#B25E3B] hover:bg-[#9C4E2D] text-[#FAF7F2] font-semibold px-6 py-3.5 transition-colors duration-200"
            >
              View submissions
            </button>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-[#B25E3B]">{error}</p>
            )}
          </form>
        </div>
      </div>
    );
  }

  const optedIn = submissions.filter((s) => s.email).length;
  const filtered = submissions.filter(
    (s) => (!filterQ1 || s.q1 === filterQ1) && (!filterQ4 || s.q4 === filterQ4)
  );

  return (
    <div data-testid="admin-dashboard" className="w-full max-w-3xl mx-auto px-4 pb-16">
      <h1 className="font-serif-display text-3xl text-[#2C221E] mb-2">Quiz submissions</h1>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <p data-testid="admin-stats" className="text-sm text-[#85746C]">
          {filtered.length} shown · {submissions.length} total · {optedIn} left an email
        </p>
        {submissions.length > 0 && (
          <button
            data-testid="admin-export-csv-btn"
            onClick={exportCSV}
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#415A4F] text-[#415A4F] hover:bg-[#EDF3F0] text-sm font-semibold px-5 py-2.5 transition-colors duration-200"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        )}
      </div>

      {submissions.length === 0 && (
        <div data-testid="admin-empty" className="flex flex-col items-center gap-3 rounded-3xl bg-white border border-[#E9DFD6] p-12 text-center">
          <Inbox className="w-8 h-8 text-[#85746C]" />
          <p className="text-[#85746C]">No submissions yet. They'll appear here as people take the quiz.</p>
        </div>
      )}

      {submissions.length > 0 && (
        <div data-testid="admin-filters" className="flex flex-col gap-3 mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#85746C] w-24 shrink-0">Stuck on</span>
            <Chip active={!filterQ1} onClick={() => setFilterQ1(null)} label="All" testId="admin-filter-q1-all" />
            {Q1_KEYS.map((k) => (
              <Chip
                key={k}
                active={filterQ1 === k}
                onClick={() => setFilterQ1(filterQ1 === k ? null : k)}
                label={Q_LABELS[k]}
                testId={`admin-filter-q1-${k}`}
              />
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#85746C] w-24 shrink-0">Readiness</span>
            <Chip active={!filterQ4} onClick={() => setFilterQ4(null)} label="All" testId="admin-filter-q4-all" />
            {[1, 2, 3, 4, 5].map((n) => (
              <Chip
                key={n}
                active={filterQ4 === n}
                onClick={() => setFilterQ4(filterQ4 === n ? null : n)}
                label={`${n}`}
                testId={`admin-filter-q4-${n}`}
              />
            ))}
          </div>
        </div>
      )}

      {submissions.length > 0 && filtered.length === 0 && (
        <p data-testid="admin-no-filter-matches" className="text-sm text-[#85746C] text-center py-8">
          No submissions match those filters — try widening the net.
        </p>
      )}

      <div className="flex flex-col gap-4">
        {[...filtered].sort((a, b) => score(b) - score(a)).map((s) => (
          <div
            key={s.id}
            data-testid={`admin-submission-${s.id}`}
            className="rounded-2xl bg-white border border-[#E9DFD6] p-5 sm:p-6"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <span className="text-sm font-semibold text-[#2C221E]">
                {s.name || s.email ? (
                  <>
                    {s.name && <span data-testid={`admin-name-${s.id}`}>{s.name} · </span>}
                    {s.email && <span data-testid={`admin-email-${s.id}`} className="text-[#B25E3B]">{s.email}</span>}
                  </>
                ) : (
                  <span className="text-[#85746C] font-normal">Anonymous — no email given</span>
                )}
              </span>
              <span className="flex items-center gap-2 text-xs text-[#85746C]">
                {s.booked && (
                  <span data-testid={`admin-booked-${s.id}`} className="rounded-full bg-[#EDF3F0] text-[#415A4F] font-semibold px-3 py-1 uppercase tracking-wide">
                    Booked
                  </span>
                )}
                {!s.booked && s.q4 >= 4 && (DURATION_WEIGHT[s.q3] || 0) >= 2 && (
                  <span data-testid={`admin-hot-lead-${s.id}`} className="rounded-full bg-[#FBEBE4] text-[#B25E3B] font-semibold px-3 py-1 uppercase tracking-wide">
                    Follow up first
                  </span>
                )}
                {!s.booked && Math.floor((Date.now() - new Date(s.timestamp).getTime()) / 86400000) >= 7 && (
                  <span data-testid={`admin-stale-${s.id}`} className="rounded-full bg-[#D9822B]/15 text-[#9C4E2D] font-semibold px-3 py-1 uppercase tracking-wide">
                    Needs a nudge · {Math.floor((Date.now() - new Date(s.timestamp).getTime()) / 86400000)}d
                  </span>
                )}
                {new Date(s.timestamp).toLocaleString()}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-[#5A4D46]">
              <p><span className="text-[#85746C]">Stuck on:</span> {Q_LABELS[s.q1] || s.q1}</p>
              <p><span className="text-[#85746C]">Stopping them:</span> {Q_LABELS[s.q2] || s.q2}</p>
              <p><span className="text-[#85746C]">Duration:</span> {Q_LABELS[s.q3] || s.q3}</p>
              <p><span className="text-[#85746C]">Readiness:</span> {s.q4}/5</p>
            </div>
            <p className="text-xs text-[#85746C] mt-3">
              Result shown: <span className="font-medium">{s.result_id}</span>
            </p>
            {s.email && (
              <button
                data-testid={`admin-booked-btn-${s.id}`}
                onClick={() => toggleBooked(s)}
                className={`mt-3 rounded-full text-sm font-semibold px-5 py-2 transition-colors duration-150 border-2 ${
                  s.booked
                    ? "border-[#415A4F] text-[#415A4F] hover:bg-[#EDF3F0]"
                    : "border-[#B25E3B] text-[#B25E3B] hover:bg-[#FBEBE4]"
                }`}
              >
                {s.booked ? "Booked — tap to undo" : "Mark as booked"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
